/**
 * What: The one place Shopify content HTML is read, written or escaped (descriptions, metafields, previews, UI markup).
 * Transport / data-flow: Pure helpers shared by the Shopify Worker, the Shopify app, Web promotion and these contracts.
 * `html-handling-boundary.test.ts` fails if another file strips tags or handles entities by hand.
 *
 * Policy: plain text only. Paragraph breaks (one or more newlines) become separate <p> blocks and
 * every paragraph is HTML-escaped. No markup from the source text is ever emitted as live HTML.
 *
 * The opposite direction lives here too, so plain text and HTML are never confused: study stimuli carry PLAIN
 * text. `descriptionHtmlToPlainText` reads a Shopify HTML description (the control) and
 * `normalizePlainDescriptionText` reads an authored plain replacement. Plain text is never treated as HTML, so a
 * replacement such as `Dries in <10 min, lasts >5 hrs` reaches the arm exactly as written.
 */
import { compile } from 'html-to-text';
/** Escapes the five HTML-significant characters so authored text can never inject markup. */
export function escapeHtmlText(value) {
    return value
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
}
/**
 * Converts winning plain text into escaped paragraph HTML (`<p>…</p>` per paragraph).
 * Returns an empty string when the text has no printable content.
 */
export function descriptionTextToParagraphHtml(text) {
    return text
        .replace(/\r\n?/g, '\n')
        .split(/\n+/)
        .map((paragraph) => paragraph.trim())
        .filter((paragraph) => paragraph.length > 0)
        .map((paragraph) => `<p>${escapeHtmlText(paragraph)}</p>`)
        .join('');
}
// Use built-in formatters only: no custom HTML parser, tree walker or entity decoder.
const convertDescriptionHtml = compile({
    wordwrap: false,
    limits: { maxInputLength: undefined }, // Never silently truncate study content.
    whitespaceCharacters: ' \t\r\n\f\u200b\u00a0',
    selectors: [
        { selector: 'a', format: 'inline' }, // Keep link text, not appended URLs.
        ...['td + td', 'td + th', 'th + td', 'th + th'].map((selector) => ({
            selector,
            format: 'inlineSurround',
            options: { prefix: ' ' },
        })),
        ...['img', 'script', 'style', 'template'].map((selector) => ({ selector, format: 'skip' })),
        ...[
            'div',
            'li',
            'ul',
            'ol',
            'h1',
            'h2',
            'h3',
            'h4',
            'h5',
            'h6',
            'blockquote',
            'section',
            'article',
            'table',
            'tr',
        ].map((selector) => ({ selector, format: 'block', options: { leadingLineBreaks: 2, trailingLineBreaks: 2 } })),
    ],
});
/**
 * Converts Shopify description HTML into study text. The HTML parser decodes references once, handles quoted
 * attributes and replaces invalid numeric references using HTML rules. Escaped `&lt;b&gt;` stays literal `<b>`.
 * Block boundaries become blank lines, `<br>` a line break, and cells are separated by spaces. Inline tags add no
 * spaces; source line wrapping is ignored. Comments and script/style/template contents are omitted.
 */
export function descriptionHtmlToPlainText(html) {
    return convertDescriptionHtml(html);
}
/** Same as `descriptionHtmlToPlainText`, with all whitespace collapsed to single spaces for one-line display. */
export function htmlToOneLineText(html) {
    return descriptionHtmlToPlainText(html).replace(/\s+/g, ' ').trim();
}
const HTML_TAG_PATTERN = /<\s*\/?\s*[a-z][^>]*>/i;
/** True when a value of unknown kind (for example a text metafield) contains an HTML tag. */
export function looksLikeHtml(value) {
    return HTML_TAG_PATTERN.test(value);
}
/**
 * Shopify description HTML, or the plain description encoded as escaped paragraph HTML (the apply route's encoding).
 * Plain text is never labelled as HTML, so `Fits <S> to <XL>` survives a later conversion.
 */
export function descriptionHtmlOrParagraphs(product) {
    const plain = product.description;
    return product.descriptionHtml ?? (typeof plain === 'string' ? descriptionTextToParagraphHtml(plain) : null);
}
/**
 * Normalizes an authored plain-text description: line endings and outer whitespace only. Nothing is stripped, so
 * `<`, `>`, `&`, entities written literally and paragraph breaks all reach the arm unchanged.
 */
export function normalizePlainDescriptionText(text) {
    return text.replace(/\r\n?/g, '\n').trim();
}
//# sourceMappingURL=description-html.js.map